import { Box, Button, HStack, ModalProps, Text } from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import SimplePopup from "components/SimplePopup";
import { useCallback, useRef, useState } from "react";
import * as traitsApi from "api/termsApi";
import useSnackbar from "hooks/useSnackbar";
import { termsHTML, termsText } from "config/terms";
import { useSignMessage } from "wagmi";

type TermsConsentPopupProps = Omit<ModalProps, "children"> & {
  address: string;
  tokenIds: number[];
  isSignatureRequired?: boolean;
  onSuccess: () => void;
  onError?: () => void;
};

const TermsConsentPopup = ({
  address,
  tokenIds,
  isSignatureRequired = true,
  onSuccess,
  onError,
  onClose,
  ...rest
}: TermsConsentPopupProps) => {
  const translate = useTranslate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isRead, setIsRead] = useState(true);
  const snackbar = useSnackbar();

  const [, sign] = useSignMessage({
    message: termsText,
  });

  const handleScroll = useCallback(() => {
    if (contentRef.current && !isRead) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        setIsRead(true);
      }
    }
  }, [isRead]);

  const handleAgree = useCallback(async () => {
    try {
      if (isSignatureRequired) {
        const { data: signature } = await sign();
        if (signature) {
          await traitsApi.acceptTerms({
            address,
            signature,
            tokenIds,
          });

          onSuccess();
        }
      } else {
        await traitsApi.acceptTerms({
          address,
          tokenIds,
        });
        onSuccess();
      }
    } catch (error: any) {
      snackbar("error", error?.message || translate("error:default"));
      onError?.();
    }
  }, [
    tokenIds,
    address,
    snackbar,
    translate,
    sign,
    onSuccess,
    onError,
    isSignatureRequired,
  ]);

  return (
    <SimplePopup
      size="lg"
      title={translate("terms:title")}
      onClose={onClose}
      {...rest}
    >
      <Box
        mt="5"
        px="3"
        py="6"
        bg="dark.700"
        borderRadius="lg"
        maxH="400px"
        fontSize="xs"
        overflowY="scroll"
        ref={contentRef}
        onScroll={handleScroll}
      >
        <Text
          opacity="0.8"
          dangerouslySetInnerHTML={{ __html: termsHTML }}
        ></Text>
      </Box>

      <HStack w="full" spacing="4" mt="6">
        <Button flex="1" colorScheme="dark" onClick={onClose}>
          {translate("common:cancel")}
        </Button>

        <Button flex="1" isDisabled={!isRead} onClick={handleAgree}>
          {translate("common:agree")}
        </Button>
      </HStack>
    </SimplePopup>
  );
};

export default TermsConsentPopup;
